import { supabase } from '@/shared/lib/supabase';
import {
  applyWindow,
  probeLimit,
  sastRangeToTimestamps,
  type ListWindow,
} from '@/shared/lib/listWindow';
import type { PeriodRange } from '@/shared/hooks/useFilterParams';

/**
 * adminBillingService — staff-side cross-customer reads on invoice + payment +
 * proof_of_payment, plus the verify/reject flow that calls the deployed
 * `proof-of-payment-verify` EF.
 *
 * Reads go through the security_invoker views v_admin_invoices_all,
 * v_admin_pending_proofs, v_admin_recent_payments — which join
 * customer_profile.full_name and auth.users.email server-side via the
 * public.user_email(uid) helper. The browser must NOT call
 * supabase.auth.admin.* (would 401 — service-role key isn't shipped to
 * the client per CLAUDE.md §4.4).
 */

export type AdminInvoiceRow = {
  id: string;
  number: string;
  customer_id: string;
  customer_name: string | null;
  customer_email: string | null;
  invoice_kind: string;
  issue_date: string;
  due_date: string;
  lifecycle_state: string;
  total: number;
  currency: string;
  paid_at: string | null;
  grace_until: string | null;
  suspended_at: string | null;
  /** PF-Q3 (2026-06-17). True when created already-paid by a PayFast cycle. */
  is_auto_paid: boolean;
  /** QM (2026-07-17). 'quotation' = QUO- document awaiting acceptance-by-payment. */
  doc_type: 'quotation' | 'invoice';
  quote_number: string | null;
  quote_expires_at: string | null;
  /**
   * Bearer token for the public /pay/:id route (2026-07-27). Powers the
   * copy-pay-link action for the admin/support tier. Never render it as text —
   * pass it through buildPayUrl().
   */
  payment_token: string | null;
  /**
   * True when &autopay=1 would succeed: a hosting_renewal on a monthly active
   * plan whose account has no live PayFast mandate. Computed by
   * public.invoice_autopay_eligible(), which mirrors every 409 in
   * hosting-subscription-public-initiate. Never re-derive this client-side —
   * getting it wrong enrols an already-subscribed customer twice.
   */
  autopay_eligible: boolean;
};

export type AdminProofRow = {
  id: string;
  customer_id: string;
  customer_name: string | null;
  customer_email: string | null;
  invoice_id: string;
  invoice_number: string;
  invoice_total: number;
  /** Null for agent-raised attest-only capture requests (no file). */
  storage_path: string | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  uploaded_at: string;
  status: 'pending' | 'verified' | 'rejected' | string;
  staff_note: string | null;
  /** Non-null when a sales agent raised this as an EFT capture request (2026-07-24). */
  submitted_by_sales: string | null;
  submitted_by_sales_email: string | null;
  /** Agent's EFT reference / date / note (distinct from staff_note). */
  submission_note: string | null;
};

/**
 * `payment.gateway` -> a human label. THE canonical map for this column.
 *
 * There were two, and they disagreed. `AdminPaymentsPage` carried
 * `{ payfast, eft, manual }` while the live column holds `payfast`,
 * `eft_manual`, `staff_adjustment` and `credit_balance` — so two of its three
 * keys matched nothing and 20 of 39 payments rendered the raw enum through the
 * `?? row.gateway` escape. The dashboard carried a third, different map.
 *
 * It lives here because this module owns `AdminPaymentRow.gateway`. The
 * dashboard re-exports it rather than declaring its own.
 *
 * An unknown key must still fall through to the raw value — a new gateway
 * showing its enum is a missing label, and better than a blank cell.
 */
export const PAYMENT_GATEWAY_LABELS: Record<string, string> = {
  payfast: 'PayFast',
  eft_manual: 'Manual EFT',
  staff_adjustment: 'Staff adjustment',
  credit_balance: 'Credit balance',
};

export type AdminPaymentRow = {
  id: string;
  customer_id: string;
  customer_name: string | null;
  invoice_id: string | null;
  invoice_number: string | null;
  amount: number;
  gateway: string;
  gateway_event_id: string | null;
  status: string;
  paid_at: string | null;
  created_at: string;
};

interface InvoiceViewRow extends Omit<AdminInvoiceRow, 'total'> {
  total: string | number;
}

interface ProofViewRow extends Omit<AdminProofRow, 'invoice_total'> {
  invoice_total: string | number;
}

interface PaymentViewRow extends Omit<AdminPaymentRow, 'amount'> {
  amount: string | number;
  customer_email: string | null;
}

/** RF (2026-07-17) — refund recording via the refund-record EF. */
export interface RecordRefundInput {
  payment_id: string;
  refund_kind: 'full' | 'partial' | 'goodwill_credit';
  amount?: number;
  method: 'payfast_portal' | 'eft' | 'credit_balance';
  external_reference?: string;
  reason: string;
  cancel_subscription_ack?: boolean;
}

export interface RecordRefundResult {
  ok: true;
  payment_id: string;
  refund_kind: string;
  amount: number;
  invoice_number: string | null;
  credit_note_number: string | null;
  emailed_to: string | null;
}

/** Thrown (as Error with .code) when the EF refuses pending the token ack. */
export interface RefundEfError extends Error {
  code?: string;
  liveTokens?: string[];
}

export type AdminCreditNoteRow = {
  id: string;
  number: string;
  customer_id: string;
  /** Resolved by a batch lookup in `listCreditNotes` — `credit_note.customer_id`
   *  references auth.users, so PostgREST cannot embed it. `null` means the
   *  lookup did not resolve; render it as `—`, not as an unnamed customer. */
  customer_name?: string | null;
  related_invoice_id: string | null;
  amount: number;
  reason: string | null;
  issued_at: string;
};

export const adminBillingService = {
  async recordRefund(input: RecordRefundInput): Promise<RecordRefundResult> {
    const { data, error } = await supabase.functions.invoke<RecordRefundResult>('refund-record', {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
      body: input,
    });
    if (error) {
      let code = '';
      let message = error.message;
      let liveTokens: string[] | undefined;
      try {
        const ctx = (error as { context?: { response?: Response } }).context;
        if (ctx?.response) {
          const b = await ctx.response.clone().json();
          if (b?.code) code = b.code;
          if (b?.message) message = b.message;
          if (b?.details?.live_tokens) liveTokens = b.details.live_tokens as string[];
        }
      } catch { /* ignore */ }
      const err = new Error(message) as RefundEfError;
      err.code = code;
      err.liveTokens = liveTokens;
      throw err;
    }
    if (!data?.ok) throw new Error('Refund could not be recorded.');
    return data;
  },

  /** Credit notes, newest first, windowed. AR-5 + AR-6 — see `listInvoices`. */
  async listCreditNotes(
    opts: { range?: PeriodRange; limit?: number } = {},
  ): Promise<ListWindow<AdminCreditNoteRow>> {
    const limit = opts.limit ?? 200;
    let cnq = supabase
      .from('credit_note')
      .select('id, number, customer_id, related_invoice_id, amount, reason, issued_at')
      .order('issued_at', { ascending: false })
      .limit(probeLimit(limit));

    // `issued_at` is a timestamptz — widen the SAST day keys to real instants.
    const cnBounds = sastRangeToTimestamps(opts.range ?? { start: null, end: null });
    if (cnBounds.since) cnq = cnq.gte('issued_at', cnBounds.since);
    if (cnBounds.until) cnq = cnq.lte('issued_at', cnBounds.until);

    const { data, error } = await cnq;
    if (error) throw new Error(`Could not load credit notes: ${error.message}`);
    // Trim before the name lookup so the AR-6 probe row costs no extra id.
    const cnWin = applyWindow(
      ((data ?? []) as Array<AdminCreditNoteRow & { amount: string | number }>).map(
        (r) => ({ ...r, amount: Number(r.amount) }),
      ),
      limit,
    );
    const rows = cnWin.rows;
    if (rows.length === 0) return cnWin;

    // F-18. The list showed a number, a date, an amount and a reason — but not
    // WHOSE credit it was, so answering the first question anyone asks required
    // opening a row. `credit_note.customer_id` references auth.users, not
    // customer_profile, so PostgREST cannot embed the name; batch-fetch it, the
    // same shape adminTicketService.list() already uses for its enrichment.
    const ids = [...new Set(rows.map((r) => r.customer_id))];
    const { data: profiles } = await supabase
      .from('customer_profile')
      .select('id, full_name')
      .in('id', ids);
    const byId = new Map(
      ((profiles ?? []) as Array<{ id: string; full_name: string }>).map((p) => [p.id, p.full_name]),
    );
    // `null` when the lookup could not resolve, never a blank — the page
    // renders that as an em-dash rather than as an unnamed customer.
    return {
      ...cnWin,
      rows: rows.map((r) => ({ ...r, customer_name: byId.get(r.customer_id) ?? null })),
    };
  },

  /**
   * Invoices, newest first, windowed.
   *
   * AR-5: the date range is applied HERE, not in the browser. `issue_date DESC
   * LIMIT 500` is a truncating window — a client-side "last year" over it would
   * render an empty table once the book passes 500 rows, which reads as "we
   * issued no invoices last year". Pass `range` and the database answers the
   * question properly.
   *
   * AR-6: asks for 501 and returns at most 500, so the page can say when there
   * is more behind the cap.
   */
  async listInvoices(
    opts: { range?: PeriodRange; limit?: number } = {},
  ): Promise<ListWindow<AdminInvoiceRow>> {
    const limit = opts.limit ?? 500;
    let q = supabase
      .from('v_admin_invoices_all')
      .select(
        'id, number, customer_id, customer_name, customer_email, invoice_kind, issue_date, due_date, lifecycle_state, total, currency, paid_at, grace_until, suspended_at, is_auto_paid, doc_type, quote_number, quote_expires_at, payment_token, autopay_eligible',
      )
      .order('issue_date', { ascending: false })
      .limit(probeLimit(limit));

    // `issue_date` is a DATE column, so it compares against the SAST day keys
    // directly — no timestamp widening, and no timezone in play.
    if (opts.range?.start) q = q.gte('issue_date', opts.range.start);
    if (opts.range?.end) q = q.lte('issue_date', opts.range.end);

    const { data, error } = await q;
    if (error) throw new Error(`Could not load invoices: ${error.message}`);
    const mapped = ((data ?? []) as InvoiceViewRow[]).map((r) => ({
      ...r,
      total: Number(r.total),
      is_auto_paid: r.is_auto_paid ?? false,
      doc_type: r.doc_type ?? 'invoice',
    }));
    return applyWindow(mapped, limit);
  },

  async listPendingProofs(): Promise<AdminProofRow[]> {
    const { data, error } = await supabase
      .from('v_admin_pending_proofs')
      .select(
        'id, customer_id, customer_name, customer_email, invoice_id, invoice_number, invoice_total, storage_path, mime_type, file_size_bytes, uploaded_at, status, staff_note, submitted_by_sales, submitted_by_sales_email, submission_note',
      )
      .order('uploaded_at', { ascending: true });
    if (error) throw new Error(`Could not load proofs: ${error.message}`);
    return ((data ?? []) as ProofViewRow[]).map((r) => ({
      ...r,
      invoice_total: Number(r.invoice_total),
    }));
  },

  /** Payments, newest first, windowed. AR-5 + AR-6 — see `listInvoices`. */
  async listRecentPayments(
    opts: { range?: PeriodRange; limit?: number } = {},
  ): Promise<ListWindow<AdminPaymentRow>> {
    const limit = opts.limit ?? 200;
    let q = supabase
      .from('v_admin_recent_payments')
      .select(
        'id, customer_id, customer_name, invoice_id, invoice_number, amount, gateway, gateway_event_id, status, paid_at, created_at',
      )
      .order('created_at', { ascending: false })
      .limit(probeLimit(limit));

    // `created_at` is a timestamptz, so the SAST day keys must be widened to
    // real instants at +02:00. A bare `Z` here would move the boundary two
    // hours and put a 00:30 SAST payment in the previous day (§4.14).
    const bounds = sastRangeToTimestamps(opts.range ?? { start: null, end: null });
    if (bounds.since) q = q.gte('created_at', bounds.since);
    if (bounds.until) q = q.lte('created_at', bounds.until);

    const { data, error } = await q;
    if (error) throw new Error(`Could not load payments: ${error.message}`);
    const mapped = ((data ?? []) as PaymentViewRow[]).map((r) => ({
      id: r.id,
      customer_id: r.customer_id,
      customer_name: r.customer_name,
      invoice_id: r.invoice_id,
      invoice_number: r.invoice_number,
      amount: Number(r.amount),
      gateway: r.gateway,
      gateway_event_id: r.gateway_event_id,
      status: r.status,
      paid_at: r.paid_at,
      created_at: r.created_at,
    }));
    return applyWindow(mapped, limit);
  },

  async sendBulkReminders(opts: {
    dryRun?: boolean;
    lifecycleStates?: string[];
  }): Promise<{
    dry_run: boolean;
    bulk_action_run_id: string;
    target_count: number;
    succeeded: number;
    failed: number;
    errors: Array<{ invoice_id: string; error: string }>;
    previewed?: Array<{ invoice_number: string; customer_id: string; total: number; due_date: string }>;
  }> {
    const { data, error } = await supabase.functions.invoke('bulk-send-payment-reminders', {
      method: 'POST',
      body: {
        dry_run: opts.dryRun ?? false,
        lifecycle_states: opts.lifecycleStates,
      },
    });
    if (error) {
      const body = (error as { context?: { body?: { message?: string } } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Bulk reminder failed.');
    }
    return data;
  },

  async verifyProof(proofId: string, opts: { decision: 'verify' | 'reject'; staff_note?: string }): Promise<{ ok: true }> {
    const { data, error } = await supabase.functions.invoke('proof-of-payment-verify', {
      method: 'POST',
      body: { proof_of_payment_id: proofId, decision: opts.decision, staff_note: opts.staff_note },
      headers: { 'idempotency-key': crypto.randomUUID() },
    });
    if (error) {
      const body = (error as { context?: { body?: { message?: string } } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Verify failed.');
    }
    return data;
  },

  async getProofDownloadUrl(storagePath: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from('proof-of-payment')
      .createSignedUrl(storagePath, 600);
    if (error) throw new Error(`Could not generate download URL: ${error.message}`);
    return data.signedUrl;
  },
};
